import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowLeft, Check } from "lucide-react";
import SectionLabel from "@/components/SectionLabel";
import PilotTimeline from "@/components/PilotTimeline";
import usePageMeta from "@/hooks/usePageMeta";
import { buildPilotMailto, ASSET_BROCHURE_URL } from "@/lib/mailto";

const opzet = [
  { t: "Één groep per pilot", d: "Bij voorkeur 20–22 deelnemers, maximaal 24 waar verantwoord. Twee trainers." },
  { t: "12 weken START", d: "Van intake en startmeting tot eindmeting en advies over een passende vervolgstap." },
  { t: "Intake & nulmeting", d: "Meting op leefgebieden, zelfredzaamheid, gezondheid en participatie." },
  { t: "START-programma", d: "Startpunt · Rugzak · Rem · Vonk · Speelruimte · LevelMoment · Volgende beweging. Groepsbijeenkomsten en persoonlijke aandacht." },
  { t: "Individuele voortgang", d: "Per deelnemer meetbaar in beeld: dagstructuur, gedrag en veerkracht." },
  { t: "Onderzoek & evaluatie", d: "Samenwerking met onafhankelijke onderzoekspartners in voorbereiding." },
  { t: "Tussen- en eindrapportage", d: "Voortgang, aanwezigheid, gedragsverandering, voor college en beleid." },
  { t: "Advies over vervolg", d: "Warme overdracht naar participatie, scholing, begeleiding of werk. VERDER alleen als het past, in overleg." },
];

const rollen = [
  {
    partij: "Gemeente",
    verantwoordelijk: [
      "Selectie en aanmelding van deelnemers",
      "Bestuurlijk commitment en interne coördinatie",
      "Toegang tot data voor nul- en eindmeting",
    ],
  },
  {
    partij: "LevelLeven",
    verantwoordelijk: [
      "Programma-uitvoering met twee trainers en buddy",
      "Monitoring, tussenmetingen en rapportages",
      "Coördinatie met lokale welzijnspartner",
    ],
  },
  {
    partij: "Onderzoeksgroep",
    verantwoordelijk: [
      "Onafhankelijk onderzoeksdesign",
      "Kwalitatieve en kwantitatieve dataverzameling",
      "Publicatie van bevindingen en effectenanalyse",
    ],
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-15%" },
  transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] },
};

export default function Pilot() {
  usePageMeta({
    title: "Pilotopzet, LevelLeven",
    description:
      "Zo ziet een LevelLeven-pilot eruit: één groep van 20–22 inwoners, 12 weken START, twee trainers, start-, tussen- en eindmeting.",
  });

  return (
    <div>
      {/* HERO */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-[#7A8276] hover:text-[#1D2A22] mb-8 transition-colors"
          >
            <ArrowLeft size={14} /> Terug naar home
          </Link>
          <div className="max-w-3xl">
            <SectionLabel>Pilotopzet</SectionLabel>
            <motion.h1
              {...fadeUp}
              className="font-heading text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[#1D2A22] font-light"
            >
              Zo ziet een{" "}
              <span className="italic text-[#E06B3F]">LevelLeven-pilot</span>{" "}
              eruit.
            </motion.h1>
            <motion.p
              {...fadeUp}
              transition={{ ...fadeUp.transition, delay: 0.15 }}
              className="mt-8 text-lg sm:text-xl text-[#4A5D4E] leading-relaxed font-light"
            >
              Eén groep per pilotgemeente: twaalf weken LevelLeven START, bij
              voorkeur 20–22 deelnemers (maximaal 24 waar verantwoord) en
              twee trainers. VERDER is geen automatisch vervolg. Samenwerking
              met onafhankelijke onderzoekspartners wordt voorbereid.
            </motion.p>
          </div>
        </div>
      </section>

      {/* OPZET GRID */}
      <section className="py-24 sm:py-32 bg-[#EFECE5]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-2xl mb-16">
            <SectionLabel>Wat de pilot omvat</SectionLabel>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1D2A22] font-light"
            >
              Acht bouwstenen,{" "}
              <span className="italic text-[#E06B3F]">één samenhangend geheel</span>.
            </motion.h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {opzet.map((o, i) => (
              <motion.div
                key={o.t}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.7, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#FAF7F1] border border-[#E5E1D8] rounded-2xl p-6"
              >
                <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-3">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="font-heading text-xl text-[#1D2A22] leading-tight">
                  {o.t}
                </p>
                <p className="mt-3 text-sm text-[#4A5D4E] leading-relaxed">
                  {o.d}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE, 12 weken START */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-2xl mb-16">
            <SectionLabel>Twaalf weken, in fasen</SectionLabel>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1D2A22] font-light"
            >
              Helder. Inpasbaar.{" "}
              <span className="italic text-[#E06B3F]">Verantwoordbaar.</span>
            </motion.h2>
          </div>
          <PilotTimeline />
        </div>
      </section>

      {/* ROLVERDELING */}
      <section className="py-24 sm:py-32 bg-[#EFECE5]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-2xl mb-16">
            <SectionLabel>Rolverdeling</SectionLabel>
            <motion.h2
              {...fadeUp}
              className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight text-[#1D2A22] font-light"
            >
              Wie doet{" "}
              <span className="italic text-[#E06B3F]">wat</span>.
            </motion.h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {rollen.map((r, i) => (
              <motion.div
                key={r.partij}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10%" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className="bg-[#FAF7F1] border border-[#E5E1D8] rounded-2xl p-8"
              >
                <p className="text-xs uppercase tracking-[0.25em] text-[#E06B3F] mb-4">
                  {String(i + 1).padStart(2, "0")}
                </p>
                <p className="font-heading text-2xl text-[#1D2A22] leading-tight">
                  {r.partij}
                </p>
                <ul className="mt-6 space-y-3">
                  {r.verantwoordelijk.map((v) => (
                    <li
                      key={v}
                      className="flex items-start gap-3 text-[#4A5D4E] font-light leading-relaxed"
                    >
                      <Check size={14} className="text-[#E06B3F] mt-1.5 shrink-0" />
                      {v}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <motion.div
            {...fadeUp}
            className="bg-[#1D2A22] text-[#FAF7F1] rounded-3xl px-8 sm:px-16 py-16 lg:py-24 grid grid-cols-1 md:grid-cols-12 gap-10 items-end"
          >
            <div className="md:col-span-7">
              <p className="text-xs uppercase tracking-[0.3em] text-[#D19B72] mb-6">
                Klaar om te verkennen
              </p>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight font-light">
                Onderzoek of een pilot{" "}
                <span className="italic text-[#D19B72]">
                  bij uw gemeente past
                </span>.
              </h2>
              <p className="mt-6 text-[#FAF7F1]/70 max-w-xl">
                Vrijblijvend kennismakingsgesprek over doelgroep, pilotopzet
                en mogelijkheden. Of download eerst de brochure.
              </p>
            </div>
            <div className="md:col-span-5 md:text-right flex flex-col sm:flex-row md:flex-col md:items-end gap-4">
              <a
                data-testid="pilot-cta-contact"
                href={buildPilotMailto()}
                className="group inline-flex items-center gap-4 bg-[#E06B3F] hover:bg-[#FAF7F1] hover:text-[#1D2A22] text-white rounded-full pl-7 pr-2 py-2 transition-colors duration-300"
              >
                <span className="text-base font-medium">Plan een verkennend gemeentegesprek</span>
                <span className="bg-[#FAF7F1] text-[#1D2A22] group-hover:bg-[#E06B3F] group-hover:text-white w-11 h-11 rounded-full flex items-center justify-center transition-colors">
                  <ArrowUpRight size={18} />
                </span>
              </a>
              <a
                data-testid="pilot-cta-brochure"
                href={ASSET_BROCHURE_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 border border-[#FAF7F1]/40 text-[#FAF7F1] hover:bg-[#FAF7F1] hover:text-[#1D2A22] rounded-full px-6 py-3 transition-colors"
              >
                Download brochure
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
